import { Flex, Row } from 'antd';
import DateRangeFilter from 'components/DateRange';
import MultiSelect from 'components/MultiSelect';
import SearchBox from 'components/SearchBox';
import SelectComponent from 'components/Select';
import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import SeasonIndexFilter from 'components/UniversalFilters/SeasonIndexFilter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderLayout from 'screens/system-utils/shared/components/PageHeaderLayout';
import testId from 'utils/testId';
import useCountryAndSeasonIndexFilter from '../../../hooks/useCountryAndSeasonIndexFilter';
import InvoiceTable from './components/InvoiceTable';
import useInvoiceFilters from './hooks/useInvoiceFilters';
import useInvoiceList from './hooks/useInvoiceList';

const InvoiceListPage = () => {
  const { t } = useTranslation();

  const {
    currentCountry: country,
    setCurrentCountry: setCountry,
    currentSeasonIdx: seasonIdx,
    setCurrentSeasonIdx: setSeasonIdx,
    seasonIndexFilterOptions,
  } = useCountryAndSeasonIndexFilter();

  const {
    currentSearchedInvoice,
    setCurrentSearchedInvoice,
    currentInvoice,
    setCurrentInvoice,
    estHarvestDateRange,
    setEstHarvestDateRange,
    currentPaymentStatus,
    setCurrentPaymentStatus,
    paymentStatusFilterOptions,
    agronomistFilterOptions,
    isAgronomistListLoading,
    currentAgronomist,
    setCurrentAgronomist,
    isSubDistrictLoading,
    subDistrictOptions,
    currentSubDistrict,
    setCurrentSubDistrict,
    resetTableFilters,
  } = useInvoiceFilters(country);

  const filterParams = useMemo(() => {
    const params: Record<string, string> = {};

    if (currentInvoice && currentInvoice?.trim()?.length > 0) {
      params['textSearch.contains'] = currentInvoice;
    }

    if (
      estHarvestDateRange &&
      estHarvestDateRange[0] &&
      estHarvestDateRange[1]
    ) {
      params['dueDate.greaterThanOrEqual'] = estHarvestDateRange[0];
      params['dueDate.lessThanOrEqual'] = estHarvestDateRange[1];
    }

    if (currentPaymentStatus && currentPaymentStatus?.length > 0) {
      params['status.in'] = [currentPaymentStatus].join(',');
    } else {
      params['status.in'] = 'unpaid,partially_paid,paid,overdue,sent';
    }

    if (currentAgronomist && currentAgronomist?.length > 0) {
      params['salesPersonId.in'] = currentAgronomist.join(',');
    }

    if (currentSubDistrict && currentSubDistrict?.length > 0) {
      params['subDistrict.in'] = currentSubDistrict.join(',');
    }

    if (country?.trim()?.length) params['country.equals'] = country;

    if (seasonIdx?.trim()?.length) params['seasonTypeId.equals'] = seasonIdx!;

    return params;
  }, [
    country,
    seasonIdx,
    currentInvoice,
    estHarvestDateRange,
    currentPaymentStatus,
    currentAgronomist,
    currentSubDistrict,
  ]);

  const {
    data: invoices,
    handlePageChange,
    totalCount,
    isLoading,
    currentPage,
    pageSize,
  } = useInvoiceList(filterParams, !!filterParams['seasonTypeId.equals']);

  const handleSeasonIndexChange = (season: string) => {
    setSeasonIdx(season);
    resetTableFilters();
  };

  const handleCountryChange = (country: string) => {
    setCountry(country);
    setSeasonIdx(null);
    resetTableFilters();
  };

  return (
    <PageHeaderLayout
      pageConfig={{
        header: t('collectionInvoices.header'),
        description: t('collectionInvoices.subHeader'),
      }}
      renderFilters={() => (
        <Flex wrap="wrap" gap={20}>
          <RBCountryFilter
            selectedCountry={country!}
            onChange={handleCountryChange}
            data-testid={testId.invoiceListPage['slc-invoice-country']}
          />
          <SeasonIndexFilter
            country={country!}
            seasonIndex={seasonIdx!}
            setSeasonIndex={setSeasonIdx}
          />
        </Flex>
      )}
    >
      <Row
        className="filters"
        data-testid={testId.invoiceListPage['row-invoice-filters']}
      >
        <SearchBox
          className="search-box invoice-search-box"
          disabled={false}
          label={`Invoice`}
          value={currentSearchedInvoice}
          placeholder={`Search Invoice`}
          onSearch={setCurrentInvoice}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentSearchedInvoice(e.target.value)
          }
          data-testid={testId.invoiceListPage['inp-invoice-search']}
        />

        <DateRangeFilter
          label={'Est. Harvest Date'}
          accessibilityLabel={'Est. Harvest Date'}
          onChange={setEstHarvestDateRange}
          data-testid={testId.invoiceListPage['rng-invoice-est-harvest-date']}
        />

        <SelectComponent
          allowClear
          defaultValue={null}
          disabled={false}
          loading={false}
          label="Payment Status"
          options={paymentStatusFilterOptions}
          placeholder={'Select Status'}
          value={currentPaymentStatus}
          onChange={setCurrentPaymentStatus}
          data-testid={testId.invoiceListPage['slc-invoice-payment-status']}
        />

        <MultiSelect
          allowClear
          className="agronomist-select"
          defaultValue={null}
          disabled={isAgronomistListLoading}
          loading={isAgronomistListLoading}
          label="Agronomist"
          options={agronomistFilterOptions}
          value={currentAgronomist}
          onChange={setCurrentAgronomist}
          data-testid={testId.invoiceListPage['slc-invoice-agronomist']}
        />

        <MultiSelect
          allowClear
          className="agronomist-select"
          defaultValue={null}
          disabled={isSubDistrictLoading}
          loading={isSubDistrictLoading}
          label="Sub-district"
          options={subDistrictOptions}
          value={currentSubDistrict}
          onChange={setCurrentSubDistrict}
          data-testid={testId.invoiceListPage['slc-invoice-sub-district']}
        />
      </Row>
      <InvoiceTable
        country={country!}
        data={invoices}
        loading={isLoading}
        pagination={{
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
        }}
      />
    </PageHeaderLayout>
  );
};

export default InvoiceListPage;
